import type { ReactNode } from 'react';
import { Checkbox, FormControlLabel, InputAdornment, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

interface SearchableCheckboxListItem {
  id: string;
  name: string;
}

interface SearchableCheckboxListProps<T extends SearchableCheckboxListItem> {
  heading: string;
  info: string;
  searchPlaceholder: string;
  search: string;
  onSearchChange: (value: string) => void;
  items: T[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  noItemsText: string;
  searchSize?: 'small' | 'medium';
  // Rendered inside the scrollable list, above the checkbox rows — shown even when the filtered
  // list is empty (e.g. CategoryModal's "+ Nieuwe optie" quick-add row).
  beforeList?: ReactNode;
}

// Search box + scrollable checkbox list over a {id, name} catalog — shared by CategoryModal's
// "Opties/toebehoren" panel and OptionModal's "Gekoppelde categorieën" panel, which otherwise
// reimplemented the identical search/filter/toggle widget on opposite sides of the same link.
export default function SearchableCheckboxList<T extends SearchableCheckboxListItem>({
  heading,
  info,
  searchPlaceholder,
  search,
  onSearchChange,
  items,
  selectedIds,
  onToggle,
  noItemsText,
  searchSize,
  beforeList,
}: SearchableCheckboxListProps<T>) {
  const filteredItems = items.filter((item) =>
    item.name.toLowerCase().includes(search.trim().toLowerCase()),
  );

  return (
    <div className="rounded-2xl border border-border-strong p-4">
      <p className="font-semibold mb-1 mt-0">{heading}</p>
      <p className="text-sm text-text-secondary mt-0 mb-3">{info}</p>

      <TextField
        size={searchSize}
        placeholder={searchPlaceholder}
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        fullWidth
        className="mb-2"
        slotProps={{
          htmlInput: { 'aria-label': searchPlaceholder },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />

      <div className="max-h-56 overflow-y-auto flex flex-col">
        {beforeList}
        {filteredItems.length === 0 ? (
          <p className="text-sm text-text-secondary text-center py-4">{noItemsText}</p>
        ) : (
          filteredItems.map((item) => (
            <FormControlLabel
              key={item.id}
              control={<Checkbox checked={selectedIds.includes(item.id)} onChange={() => onToggle(item.id)} />}
              label={item.name}
            />
          ))
        )}
      </div>
    </div>
  );
}
